import React, { MouseEventHandler, useEffect } from "react";

import Typography from "@mui/material/Typography";

import useAnchorElement, {
  UseAnchorElement,
} from "@app/utils/hooks/useAnchorElement";

import ManageTags from "./ManageTags";
import { HeaderButton, Popover, PopoverContent, PopoverHeader } from "./styles";
import useManageTags, { UseManageTagsArgs } from "./useManageTags";

interface ControlProps {
  onClick: MouseEventHandler<HTMLElement>;
}

export interface ManageTagsPopoverProps {
  allTags: UseManageTagsArgs["allTags"];
  appliedTags: UseManageTagsArgs["appliedTags"];
  canApplyToSeries?: UseManageTagsArgs["canApplyToSeries"];
  onSave: UseManageTagsArgs["onSave"];
  popoverProps?: Pick<UseAnchorElement, "onClose" | "open"> & {
    anchorEl: UseAnchorElement["anchorEl"];
  };
  renderControl?: (props: ControlProps) => React.ReactNode;
}

const ManageTagsPopover = ({
  allTags,
  appliedTags,
  canApplyToSeries = false,
  onSave: onSaveProp,
  renderControl,
  popoverProps,
}: ManageTagsPopoverProps) => {
  const { onRemoveAll, onSave, ...manageTagsProps } = useManageTags({
    allTags,
    appliedTags,
    canApplyToSeries,
    onSave: onSaveProp,
  });

  const { open, onClose, onClick, anchorEl } = useAnchorElement();

  useEffect(() => {
    manageTagsProps.reset();
  }, [allTags.length, appliedTags.length, open]);

  const handleClose = () => {
    (popoverProps?.onClose ?? onClose)();
  };

  const handleSave = () => {
    onSave();
    handleClose();
  };

  const openPopover: typeof onClick = (event) => {
    onClick(event);
    // short timeout to give time for the element to appear before trying to focus it
    setTimeout(manageTagsProps.focusInput, 100);
  };

  return (
    <>
      {renderControl?.({ onClick: openPopover })}
      <Popover
        open={open}
        anchorEl={anchorEl}
        {...popoverProps}
        onClose={handleClose}
      >
        <PopoverContent spacing={1}>
          <PopoverHeader variant="apart">
            <Typography variant="bodySecondaryMed">
              Tags{appliedTags.length ? ` (${appliedTags.length})` : ""}
            </Typography>
            <HeaderButton
              color="error"
              onClick={onRemoveAll}
              disabled={manageTagsProps.appliedTags.length < 1}
            >
              Remove all
            </HeaderButton>
          </PopoverHeader>
          <ManageTags
            {...manageTagsProps}
            canApplyToSeries={canApplyToSeries}
            onSave={handleSave}
            onClose={handleClose}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export default ManageTagsPopover;
